import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { TaskSubject } from '@/types/work'

interface SubjectPickerProps {
  value: TaskSubject | null
  onChange: (subject: TaskSubject | null) => void
  disabled?: boolean
}

// Hits /work/subject_searches?q=... for the mixed donor + opportunity
// list. The endpoint is already perimeter-scoped server-side and
// returns each result as { id, type, label, sublabel }.
//
// v1 UX: a query input + a dropdown of result chips. Chip labels show
// a small type badge derived from the result's `type` (registered class
// name). Selection produces the full TaskSubject object so the form
// can store both id and type when it submits.
export function SubjectPicker({ value, onChange, disabled }: SubjectPickerProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<TaskSubject[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (query.trim() === '') {
      setResults([])
      return
    }
    const handle = setTimeout(() => {
      fetch(`/work/subject_searches?q=${encodeURIComponent(query)}`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((data) => setResults(data.results ?? []))
        .catch(() => setResults([]))
    }, 200)
    return () => clearTimeout(handle)
  }, [query])

  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-md border bg-card px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{value.label}</span>
            <Badge variant="outline" className="text-xs">
              {typeChipLabel(value.type)}
            </Badge>
          </div>
          {value.sublabel && (
            <p className="mt-0.5 text-xs text-muted-foreground">{value.sublabel}</p>
          )}
        </div>
        {!disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            Retirer
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Input
        type="search"
        placeholder="Rechercher un donateur ou une opportunité…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        disabled={disabled}
      />
      {open && results.length > 0 && (
        <ul className="max-h-60 overflow-y-auto rounded-md border bg-popover">
          {results.map((r) => (
            <li key={`${r.type}-${r.id}`}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onChange(r)
                  setQuery('')
                  setResults([])
                  setOpen(false)
                }}
              >
                <span className="flex flex-col">
                  <span className="font-medium">{r.label}</span>
                  {r.sublabel && (
                    <span className="text-xs text-muted-foreground">{r.sublabel}</span>
                  )}
                </span>
                <Badge variant="outline" className="text-xs">
                  {typeChipLabel(r.type)}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim() !== '' && results.length === 0 && (
        <p className="text-xs text-muted-foreground">Aucun résultat pour « {query} »</p>
      )}
    </div>
  )
}

// Maps the registered class name to its French display label. Kept in
// sync with crm_work.fr.yml subject_chip locale (TODO add when chips
// land in i18n).
function typeChipLabel(type: string): string {
  switch (type) {
    case 'CrmPeople::IndividualDonor':
      return 'Donateur'
    case 'CrmPeople::OrganizationDonor':
      return 'Donateur'
    case 'CrmFundraising::Opportunity':
      return 'Opportunité'
    default:
      return type
  }
}
